import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import { isServer } from '@unifyapps/network/utils/fetch/utils';
import isNative from '@unifyapps/network/utils/native/isNative';
import { isDynamicValue } from './dynamicBindings';
import {
  doNotEvaluateExpression,
  isEntityIdPresentInValue,
  type ShouldEvaluateResult,
} from './evaluateExpression';

type InterfaceFlags = InterfaceEntity['properties']['flags'];

/**
 * Picks the predicate that decides whether an evaluation result which is itself an
 * expression gets evaluated again.
 *
 * Kept free of React so the call sites that run outside the GlobalAppStateStore can
 * reach it too — notably `getInitialPageVariablesState`, which runs while the store
 * provider is still constructing its own store, and `useResolvedEnable`, which runs
 * on a minimal context with no store at all. Both only have the interface record.
 */
export function getShouldEvaluateResult(flags: InterfaceFlags): ShouldEvaluateResult {
  // SSR runs every expression inside the shared node process, so a result that is itself
  // an expression costs another eval on every request. This flag drops that recursion on
  // the server only — the browser still resolves the nested value after hydration.
  // `!isNative()` matches the guard in indirectEval/compileGet: RN has no server pass.
  if (flags?.shouldSkipReEvaluationOnServer && isServer() && !isNative()) {
    return doNotEvaluateExpression;
  }

  return flags?.shouldReEvaluateUsingEntityIds ? isEntityIdPresentInValue : isDynamicValue;
}
